import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const HealthMetric = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: #2c3e50;
  border-radius: 8px;
  border-left: 4px solid ${props => 
    props.status === 'healthy' ? '#2ecc71' :
    props.status === 'warning' ? '#f39c12' : '#e74c3c'
  };
`;

const MetricInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const MetricName = styled.span`
  color: #ecf0f1;
  font-weight: 500;
  font-size: 14px;
`;

const MetricDescription = styled.span`
  color: #95a5a6;
  font-size: 12px;
`;

const MetricValue = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Value = styled.span`
  color: #ecf0f1;
  font-weight: bold;
  font-size: 16px;
`;

const StatusIndicator = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => 
    props.status === 'healthy' ? '#2ecc71' :
    props.status === 'warning' ? '#f39c12' : '#e74c3c'
  };
`;

const ProgressBar = styled.div`
  width: 60px;
  height: 6px;
  background: #34495e;
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${props => 
    props.value > 80 ? '#e74c3c' :
    props.value > 60 ? '#f39c12' : '#2ecc71'
  };
  width: ${props => props.value}%;
  transition: width 0.3s ease;
`;

const SystemHealth = ({ metrics = {} }) => {
  const {
    cpuUsage = 45,
    memoryUsage = 62,
    diskUsage = 34,
    networkLatency = 12,
    uptime = 99.8,
    activeConnections = 156
  } = metrics;

  const getStatus = (value, thresholds = { warning: 70, critical: 85 }) => {
    if (value >= thresholds.critical) return 'critical';
    if (value >= thresholds.warning) return 'warning';
    return 'healthy';
  };

  const healthMetrics = [
    {
      name: 'CPU Usage',
      description: 'Current processor utilization',
      value: `${cpuUsage}%`,
      status: getStatus(cpuUsage),
      showProgress: true,
      progressValue: cpuUsage
    },
    {
      name: 'Memory Usage',
      description: 'RAM utilization',
      value: `${memoryUsage}%`,
      status: getStatus(memoryUsage),
      showProgress: true,
      progressValue: memoryUsage
    },
    {
      name: 'Disk Usage',
      description: 'Storage space used',
      value: `${diskUsage}%`,
      status: getStatus(diskUsage),
      showProgress: true,
      progressValue: diskUsage
    },
    {
      name: 'Network Latency',
      description: 'Average response time',
      value: `${networkLatency}ms`,
      status: getStatus(networkLatency, { warning: 20, critical: 50 }),
      showProgress: false
    },
    {
      name: 'System Uptime',
      description: 'System availability',
      value: `${uptime}%`,
      status: uptime >= 99 ? 'healthy' : uptime >= 95 ? 'warning' : 'critical',
      showProgress: false
    },
    {
      name: 'Active Connections',
      description: 'Current user sessions',
      value: activeConnections.toString(),
      status: 'healthy',
      showProgress: false
    }
  ];

  return (
    <Container>
      {healthMetrics.map((metric, index) => (
        <HealthMetric key={index} status={metric.status}>
          <MetricInfo>
            <MetricName>{metric.name}</MetricName>
            <MetricDescription>{metric.description}</MetricDescription>
          </MetricInfo>
          <MetricValue>
            <Value>{metric.value}</Value>
            {metric.showProgress ? (
              <ProgressBar>
                <ProgressFill value={metric.progressValue} />
              </ProgressBar>
            ) : (
              <StatusIndicator status={metric.status} />
            )}
          </MetricValue>
        </HealthMetric>
      ))}
    </Container>
  );
};

export default SystemHealth; 